/* BCF C2 gallery: slide swap, safe-box caption fit, mini-preview strip, and
   the fit slide (the fit photo for the selected length).
   Markup: snippets/bcf-c2-gallery.liquid. Emits bcf:c2-gallery-show {index}.
   Listens: bcf:variant-change (root or document), change on [name="id"] in the
   C2 root, popstate (?variant=), bcf:c2-offer-change {stack,totalCents}, and
   bcf:c2-gallery-show {index, scroll, len} dispatched by anything outside the
   gallery (e.g. a length chip): sets the fit slide to len (when given), shows
   that slide without re-emitting and, with scroll, brings the frame into view
   only when it is not already on screen. Variant changes update the fit
   slide's photo without showing it.
   Pure helpers are exported as BCFC2Gallery (unit tests: scripts/c2-gallery.test.mjs). */
(function (glob) {
  // Index of a "lengths" slide (a lengths type, or an asset / existingAsset
  // filename containing "lengths"), ignoring the hero. -1 when there is none.
  function lengthsIndex(data) {
    if (!Array.isArray(data)) return -1;
    return data.findIndex((e, i) => i > 0 && !!e && (e.type === 'lengths' || /lengths/i.test(String(e.existingAsset || '')) || /lengths/i.test(String(e.asset || ''))));
  }
  const hasFit = (fit) => !!fit && typeof fit === 'object' && Object.keys(fit).some((k) => fit[k] && fit[k].asset);
  // Where the fit slide goes: the lengths slide's index (it replaces it), else
  // 1 (inserted right after the hero). -1 without fit data or gallery data.
  function fitSlideIndex(data, fit) {
    if (!Array.isArray(data) || !data.length || !hasFit(fit)) return -1;
    const li = lengthsIndex(data);
    return li > 0 ? li : 1;
  }
  const fitReplaces = (data, fit) => fitSlideIndex(data, fit) > 0 && lengthsIndex(data) > 0;
  // The fit slide's default caption box (percent of the image); the data's per-product box wins.
  const FIT_BOX = { l: 4, t: 5, w: 26 };
  // Fit photo for a length: that length, else 20″, else the first one. {len, asset, assetThumb, box} or null.
  function fitAsset(fit, len) {
    if (!hasFit(fit)) return null;
    const keys = Object.keys(fit).filter((k) => fit[k] && fit[k].asset);
    const k = [String(len), '20'].find((x) => keys.includes(x)) || keys[0];
    const b = fit[k].box;
    return { len: k, asset: fit[k].asset, assetThumb: fit[k].assetThumb || fit[k].asset, box: b && typeof b.l === 'number' ? { l: b.l, t: b.t, w: b.w } : { ...FIT_BOX } };
  }
  // Caption and alt for the fit slide. Rope widths read with one decimal (2.0 mm).
  // A no-break space keeps "the neck" (and "<n> mm") together when the caption wraps.
  function fitCaption(len, width, family) {
    const fam = String(family || '').trim();
    const Fam = fam ? fam.charAt(0).toUpperCase() + fam.slice(1) : '';
    let w = String(width || '').trim();
    if (w && fam.toLowerCase() === 'rope' && /^\d+$/.test(w)) w += '.0';
    const name = (w ? w + ' mm ' + Fam : Fam).trim();
    return { headline: len + '″ on the\u00a0neck', subline: name.replace(' mm', '\u00a0mm'), alt: (name + ' chain at ' + len + ' inches on the neck').trim() };
  }
  // The fit slide's gallery entry for a length.
  function fitEntry(fit, len, meta) {
    const a = fitAsset(fit, len);
    if (!a) return null;
    const c = fitCaption(a.len, meta && meta.width, meta && meta.family);
    return { asset: a.asset, assetThumb: a.assetThumb, alt: c.alt, headline: c.headline, subline: c.subline, box: a.box, type: 'fit', len: a.len };
  }
  // Gallery data with the fit slide placed: {slides, index}. Never mutates data.
  function withFit(data, fit, len, meta) {
    const list = Array.isArray(data) ? data.slice() : [];
    const index = fitSlideIndex(list, fit);
    if (index < 0) return { slides: list, index: -1 };
    const e = fitEntry(fit, len, meta);
    if (fitReplaces(list, fit)) list[index] = e;
    else list.splice(index, 0, e);
    return { slides: list, index };
  }
  // The frame counts as on screen when at least half of it (or half the viewport,
  // for a frame taller than the viewport) is visible.
  function needsScroll(rect, viewportH) {
    const visible = Math.min(rect.bottom, viewportH) - Math.max(rect.top, 0);
    return visible < Math.min(rect.height, viewportH) / 2;
  }
  glob.BCFC2Gallery = { lengthsIndex, fitSlideIndex, fitAsset, fitCaption, fitEntry, withFit, needsScroll, FIT_BOX };
  if (!glob.document) return;

  const SIZES = [24, 21, 18];
  const baseSize = (w) => (w < 32 ? 18 : w < 50 ? 21 : 24);
  const reduced = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const money = (c) => '$' + (c % 100 ? (c / 100).toFixed(2) : String(c / 100));
  const json = (el) => { try { return el ? JSON.parse(el.textContent) : null; } catch (e) { return null; } };

  function init(g) {
    if (g.dataset.c2Ready) return;
    g.dataset.c2Ready = '1';
    const root = g.closest('[data-c2-pdp]') || document.body;
    const frame = g.querySelector('[data-c2-frame]');
    const main = g.querySelector('[data-c2-main]');
    const cap = g.querySelector('[data-c2-cap]');
    const head = g.querySelector('[data-c2-cap-head]');
    const sub = g.querySelector('[data-c2-cap-sub]');
    const count = g.querySelector('[data-c2-count]');
    let thumbs = Array.from(g.querySelectorAll('[data-c2-thumb]'));
    const variants = json(g.querySelector('[data-c2-variants]')) || {};
    const strip = g.querySelector('[data-c2-strip]');
    const group = g.querySelector('.c2-thumbs');
    let cur = 0;

    /* Fit slide: the fit photo for the selected length (server-rendered thumb). */
    const base = g.dataset.assetBase || '';
    const fitData = json(g.querySelector('#bcf-c2-fit-data'));
    const meta = { width: root.dataset.c2Width, family: root.dataset.c2Family };
    const fitThumb = g.querySelector('[data-c2-fit-thumb]');
    let fitLen = fitThumb ? String(fitThumb.dataset.c2FitLen || '20') : '';
    const raw = json(g.querySelector('#bcf-c2-gallery-data')) || [];
    const placed = withFit(raw, fitData, fitLen, meta);
    // The markup and the rule must agree; if not, run without the fit slide and
    // drop its thumb so every other thumb lines up with its caption again.
    const fitIdx = fitThumb && placed.index >= 0 && thumbs[placed.index] === fitThumb && placed.slides.length === thumbs.length ? placed.index : -1;
    const data = fitIdx >= 0 ? placed.slides : raw;
    if (fitIdx < 0 && fitThumb) {
      thumbs = thumbs.filter((t) => t !== fitThumb);
      fitThumb.remove();
      thumbs.forEach((t, i) => t.setAttribute('aria-label', 'Show image ' + (i + 1) + ' of ' + thumbs.length));
      if (count) count.textContent = '1 / ' + thumbs.length;
    }
    // Only a length the fit data has (18/20/22/24) moves the fit slide.
    const isFitLen = (n) => n != null && !!fitData && !!fitData[String(n)];

    function fit() {
      const d = data[cur];
      if (!cap || !d || !d.box || !d.headline) { if (cap) cap.hidden = true; return; }
      cap.hidden = false;
      const h = frame.clientHeight;
      // Keep clear of the "n / N" counter when the box reaches the right edge.
      const gap = count && d.box.l + d.box.w > 70 ? 26 : 8;
      const avail = Math.max(0, h * (1 - d.box.t / 100) - gap);
      cap.style.maxHeight = avail + 'px';
      let size = baseSize(d.box.w);
      for (const s of SIZES) {
        if (s > size) continue;
        size = s;
        head.style.fontSize = s + 'px';
        if (cap.scrollHeight <= avail && head.scrollWidth <= head.clientWidth) break;
      }
      cap.toggleAttribute('data-clipped', cap.scrollHeight > avail);
    }

    function show(i, emit = true) {
      if (!thumbs.length || !main) return;
      i = (i + thumbs.length) % thumbs.length;
      const t = thumbs[i];
      cur = i;
      main.src = t.dataset.src;
      main.alt = t.dataset.alt || '';
      main.removeAttribute('srcset');
      thumbs.forEach((b, j) => (j === i ? b.setAttribute('aria-current', 'true') : b.removeAttribute('aria-current')));
      // Keep the active thumb inside the thumb strip (horizontal scroll only, never the page).
      if (group) {
        const d = t.getBoundingClientRect().left - group.getBoundingClientRect().left;
        if (d < 0 || d + t.offsetWidth > group.clientWidth) group.scrollLeft += d - 8;
      }
      if (count) count.textContent = i + 1 + ' / ' + thumbs.length;
      const d = data[i];
      if (cap && d && d.box && d.headline) {
        cap.style.left = d.box.l + '%';
        cap.style.top = d.box.t + '%';
        cap.style.width = d.box.w + '%';
        cap.classList.toggle('c2-cap--right', d.box.l > 45);
        head.textContent = d.headline;
        sub.textContent = d.subline || '';
      } else if (cap) {
        cap.hidden = true;
      }
      fit();
      updateStrip();
      // Warm the next slide so the following tap is instant.
      const n = thumbs[(i + 1) % thumbs.length];
      if (n && n.dataset.src) new Image().src = n.dataset.src;
      if (emit) g.dispatchEvent(new CustomEvent('bcf:c2-gallery-show', { bubbles: true, detail: { index: i } }));
    }

    // Point the fit slide at a length: thumb, data entry and (when it is the
    // active slide) the main image and caption. Never changes the active slide.
    function setFitLen(n) {
      if (fitIdx < 0 || !isFitLen(n)) return;
      const e = fitEntry(fitData, n, meta);
      if (!e || e.len === fitLen) return;
      fitLen = e.len;
      data[fitIdx] = e;
      const t = thumbs[fitIdx];
      t.dataset.src = base + e.asset;
      t.dataset.thumb = base + e.assetThumb;
      t.dataset.alt = e.alt;
      t.dataset.c2FitLen = e.len;
      const ti = t.querySelector('img');
      if (ti) ti.src = base + e.assetThumb;
      if (cur === fitIdx) show(fitIdx, false);
      else updateStrip();
    }
    // Warm the other three lengths' photos (480 + 1200) after the first interaction,
    // so a length tap is instant.
    let warmed = false;
    function warmFit() {
      if (warmed || fitIdx < 0) return;
      warmed = true;
      const go = () => Object.keys(fitData).forEach((k) => {
        if (k === fitLen || !fitData[k]) return;
        [fitData[k].assetThumb, fitData[k].asset].forEach((f) => { if (f) new Image().src = base + f; });
      });
      if ('requestIdleCallback' in window) window.requestIdleCallback(go, { timeout: 1500 }); else setTimeout(go, 200);
    }
    if (fitIdx >= 0) {
      ['pointerdown', 'keydown', 'touchstart', 'scroll', 'wheel'].forEach((t) => window.addEventListener(t, warmFit, { once: true, passive: true }));
      // At >=990px the theme scrolls .page-wrapper, not the window: catch any scroll.
      document.addEventListener('scroll', warmFit, { capture: true, once: true, passive: true });
    }

    thumbs.forEach((b, i) => b.addEventListener('click', () => show(i)));
    if (group) {
      group.addEventListener('keydown', (e) => {
        if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return;
        e.preventDefault();
        show(cur + (e.key === 'ArrowRight' ? 1 : -1));
        thumbs[cur].focus();
      });
    }
    let x0 = null;
    frame.addEventListener('touchstart', (e) => { x0 = e.touches[0].clientX; }, { passive: true });
    frame.addEventListener('touchend', (e) => {
      if (x0 === null) return;
      const dx = e.changedTouches[0].clientX - x0;
      x0 = null;
      if (Math.abs(dx) > 40) show(cur + (dx < 0 ? 1 : -1));
    });
    // Requests from outside the gallery (the gallery's own emits have g as target).
    root.addEventListener('bcf:c2-gallery-show', (e) => {
      if (g.contains(e.target)) return;
      const d = e.detail || {};
      const i = Number(d.index);
      if (!Number.isInteger(i) || i < 0 || i >= thumbs.length) return;
      if (d.len != null && i === fitIdx) { setFitLen(d.len); warmFit(); }
      show(i, false);
      if (d.scroll && frame && needsScroll(frame.getBoundingClientRect(), window.innerHeight || document.documentElement.clientHeight)) {
        // scrollIntoView, not window.scrollTo: at >=990px the theme scrolls .page-wrapper.
        frame.scrollIntoView({ block: 'start', behavior: reduced() ? 'instant' : 'smooth' });
      }
    });
    if ('ResizeObserver' in window) new ResizeObserver(fit).observe(frame);
    else window.addEventListener('resize', fit);
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(fit);

    /* Mini-preview strip */
    const offer = { stack: false, totalCents: null };
    let variantId = String(root.dataset.c2VariantId || '');
    const urlVariant = () => {
      try { const v = new URL(window.location.href).searchParams.get('variant'); return v && variants[v] ? v : null; } catch (e) { return null; }
    };
    variantId = urlVariant() || variantId;

    function updateStrip() {
      if (!strip) return;
      const v = variants[variantId] || {};
      const h1 = root.querySelector('.c2-h1');
      const t = thumbs[cur];
      const img = strip.querySelector('[data-c2-strip-img]');
      const src = t ? t.dataset.thumb : main && main.currentSrc;
      if (src && img.getAttribute('src') !== src) img.src = src;
      strip.querySelector('[data-c2-strip-name]').textContent = h1 ? h1.textContent.replace(/\s+/g, ' ').trim() : '';
      const price = offer.stack && offer.totalCents != null ? offer.totalCents : v.price != null ? v.price : Number(root.dataset.c2PriceCents || 0);
      strip.querySelector('[data-c2-strip-price]').textContent = money(price);
      const width = root.dataset.c2Width;
      const parts = [];
      if (width) parts.push(width + ' mm');
      if (v.len) parts.push(v.len + '″');
      parts.push(offer.stack ? 'Stack' : '1 chain');
      strip.querySelector('[data-c2-strip-meta]').textContent = parts.join(' · ');
    }

    function setVariant(id) {
      id = id == null ? '' : String(id);
      if (!variants[id] || id === variantId) return;
      variantId = id;
      setFitLen(variants[id].len);
      updateStrip();
    }
    const onVariantEvent = (e) => {
      const v = e.detail && (e.detail.variant || e.detail);
      setVariant(v && (v.id != null ? v.id : v));
    };
    root.addEventListener('bcf:variant-change', onVariantEvent);
    if (root !== document.body) document.addEventListener('bcf:variant-change', (e) => { if (!root.contains(e.target)) onVariantEvent(e); });
    root.addEventListener('change', (e) => { if (e.target && e.target.name === 'id') setVariant(e.target.value); });
    window.addEventListener('popstate', () => setVariant(urlVariant()));
    document.addEventListener('bcf:c2-offer-change', (e) => {
      const d = e.detail || {};
      offer.stack = !!d.stack;
      offer.totalCents = typeof d.totalCents === 'number' ? d.totalCents : null;
      updateStrip();
    });

    function setStrip(on) {
      if (!strip) return;
      strip.classList.toggle('is-on', on);
      strip.setAttribute('aria-hidden', on ? 'false' : 'true');
      strip.querySelector('[data-c2-strip-top]').tabIndex = on ? 0 : -1;
    }
    if (strip && 'IntersectionObserver' in window) {
      new IntersectionObserver((entries) => {
        const e = entries[entries.length - 1];
        // Fully out of view, above the viewport (scrolled past).
        setStrip(!e.isIntersecting && e.boundingClientRect.bottom <= 0);
      }, { threshold: 0 }).observe(frame);
      strip.querySelector('[data-c2-strip-top]').addEventListener('click', () => {
        // scrollIntoView, not window.scrollTo: at >=990px the theme scrolls .page-wrapper.
        frame.scrollIntoView({ block: 'start', behavior: reduced() ? 'instant' : 'smooth' });
        frame.focus({ preventScroll: true });
      });
    }

    if (variants[variantId]) setFitLen(variants[variantId].len);
    updateStrip();
    fit();
  }

  const boot = () => document.querySelectorAll('[data-c2-gallery]').forEach(init);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
  document.addEventListener('shopify:section:load', boot);
})(typeof window !== 'undefined' ? window : globalThis);
